import { useEffect, useMemo, type FC } from 'react';

import { getEnecoBeXapiSiteApiV1OrdersByOrderId } from '@eneco/dc-be/client';
import usePublicDCBE from '@eneco/dc-be/hooks/usePublicDCBE';
import unwrapData from '@eneco/dc-be/utils/unwrapData';
import { typedYupResolver } from '@eneco/hooks/form';
import { Form } from '@eneco/sparky';
import { useContent } from '@sitecore/common';
import { OrderManagementRendering } from '@sitecore/types/OrderManagement';
import type { Path } from 'react-hook-form';
import { FormProvider, useForm, useFormContext } from 'react-hook-form';

import { OrderManagementProvider } from './contexts/OrderManagementProvider';
import { OrderManagementStepperFlow } from './flows/OrderManagementStepperFlow';
import { useOrderId } from './hooks/useOrderId';
import { OrderManagementFormSchema, getOrderManagementSchema } from './types/orderManagementSchema';
import { applyOrderDefaultsToForm, getOrderManagementDefaultValues } from './utils/getOrderManagementDefaultValues';
import FlowNavigationLayout from '../../components/navigation/flow/FlowNavigationLayout';

const OrderManagement: FC = () => {
  const { fields } = useContent<OrderManagementRendering>();

  const schema = useMemo(() => getOrderManagementSchema(fields), [fields]);
  const defaultValues = useMemo(() => getOrderManagementDefaultValues(), []);

  const methods = useForm<OrderManagementFormSchema>({
    resolver: typedYupResolver<OrderManagementFormSchema>(schema),
    mode: 'onTouched',
    defaultValues,
  });

  if (Object.keys(methods.formState.errors).length > 0) console.warn(methods.formState.errors);

  return (
    <FormProvider {...methods}>
      <Form>
        <OrderManagementProvider>
          <OrderFormDefaultsFromOrder />
          <OrderManagementContent />
        </OrderManagementProvider>
      </Form>
    </FormProvider>
  );
};

export default OrderManagement;

const OrderFormDefaultsFromOrder: FC = () => {
  const { setValue, getValues } = useFormContext<OrderManagementFormSchema>();
  const orderId = useOrderId();
  const { data } = usePublicDCBE(
    getEnecoBeXapiSiteApiV1OrdersByOrderId,
    { path: { orderId: orderId ?? '' } },
    [`/orders/${orderId}`],
    !!orderId,
  );
  const orderData = unwrapData(data);

  useEffect(() => {
    if (orderData) {
      applyOrderDefaultsToForm(orderData, (path, value, opts) =>
        setValue(path as unknown as Path<OrderManagementFormSchema>, value, opts),
      );
    }
  }, [orderData, setValue, getValues]);

  return null;
};

const OrderManagementContent: FC = () => {
  return (
    <FlowNavigationLayout>
      <OrderManagementStepperFlow />
    </FlowNavigationLayout>
  );
};
